import React from 'react';
import { useForm } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../../../store/dashboardStore';

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  school: z.string().min(1, "School is required")
});

type FormData = z.infer<typeof schema>;

interface StudentFormProps {
  backgroundColor: string;
  textColor?: string;
  ShapeIcon: React.FC;
}

export const StudentForm: React.FC<StudentFormProps> = ({ 
  backgroundColor,
  textColor = "white",
  ShapeIcon 
}) => {
  const navigate = useNavigate();
  const setStudentFirstName = useDashboardStore(state => state.setStudentFirstName);
  const setSelectedShape = useDashboardStore(state => state.setSelectedShape);
  
  const { register, handleSubmit, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onChange"
  });

  const name = watch("name");
  const school = watch("school");
  const showGoButton = name && school;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: "name" | "school") => {
    const value = e.target.value;
    if (value) {
      const transformedValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setValue(field, transformedValue);
    } else {
      setValue(field, value);
    }
  };

  const onSubmitForm = (data: FormData) => {
    setStudentFirstName(data.name);
    
    // Set the selected shape based on the current page
    if (backgroundColor === '#4558C8') {
      setSelectedShape('circle');
    } else if (backgroundColor === '#DFF37D') {
      setSelectedShape('triangle');
    } else {
      setSelectedShape('square');
    }
    
    navigate('/dashboard');
  };

  return (
    <main 
      className="flex flex-col min-h-screen max-h-screen bg-white overflow-hidden font-sans px-10 pt-4"
      style={{ 
        backgroundColor,
        backgroundImage: `
          linear-gradient(${backgroundColor}55, ${backgroundColor}55),
          url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.15' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.1'/%3E%3C/svg%3E")
        `
      }}
    >
      <form onSubmit={handleSubmit(onSubmitForm)} className="px-10 pt-4">
        <div className="flex flex-col gap-[2.5vh] w-[66%] z-10">
          <div>
            <input
              {...register("name")}
              onChange={(e) => handleInputChange(e, "name")}
              className="w-full border-b-2 text-[16vh] tracking-[-3px] focus:outline-none py-[0.5vh] leading-none bg-transparent font-medium"
              style={{ 
                borderColor: textColor,
                color: textColor
              }}
            />
            <label 
              className="block text-[4vh] font-light tracking-[-2px] mt-2 mb-[0.2vh]" 
              style={{ 
                color: '#292B2D',
                fontFamily: 'SF Pro Display Light'
              }}
            >
              What Is Your First Name?
            </label>
            <p 
              className="text-[1.1vh] tracking-[-1px] uppercase leading-[0.9]"
              style={{ 
                color: '#292B2D',
                opacity: 0.5,
                fontFamily: 'SF Pro Display'
              }}
            >
              YOUR FIRST NAME ONLY - NO SURNAME REQUIRED.
            </p>
          </div>

          <div>
            <input
              {...register("school")}
              onChange={(e) => handleInputChange(e, "school")}
              className="w-full border-b-2 text-[16vh] tracking-[-3px] focus:outline-none py-[0.5vh] leading-none bg-transparent font-medium"
              style={{ 
                borderColor: textColor,
                color: textColor
              }}
            />
            <label 
              className="block text-[4vh] font-light tracking-[-2px] mt-2 mb-[0.2vh]"
              style={{ 
                color: '#292B2D',
                fontFamily: 'SF Pro Display Light'
              }}
            >
              What School Do You Go To?
            </label>
            <p 
              className="text-[1.1vh] tracking-[-1px] uppercase leading-[0.9]"
              style={{ 
                color: '#292B2D',
                opacity: 0.5,
                fontFamily: 'SF Pro Display'
              }}
            >
              USE REAL SCHOOL NAME.
            </p>
          </div>
        </div>

        <AnimatePresence>
          {showGoButton && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed bottom-0 right-0 w-[34vw] h-[50vh] flex items-end justify-end p-8"
            >
              <button
                type="submit"
                className="w-full h-full flex items-end justify-end"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-full h-full transform scale-150"
                  style={{ fill: textColor }}
                >
                  <path d="M7.5 3L6 4.5L13.5 12L6 19.5L7.5 21L16.5 12L7.5 3Z" transform="rotate(45 12 12)" />
                </svg>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <div className="absolute top-8 right-10">
        <div className="w-[11vh] h-[11vh]">
          <ShapeIcon />
        </div>
      </div>
    </main>
  );
};